/**
 * Authentication hook
 */
import { useState, useEffect } from 'react'
import {
  isAuthenticated,
  getCurrentUser,
  initiateLogin,
  logout,
  setAuthToken,
  setRefreshToken,
  meetsMinimum,
  type AuthUser,
  type UserRole,
} from '@/shared/lib/auth'

// DEV-ONLY: reads a role from the `?role=` URL query param so the UI can be
// eyeballed across `member | dev | admin` locally without running
// set_user_role.py + re-logging in. Gated behind `import.meta.env.DEV` —
// inert in production builds, where the real role from `/auth/me` is always
// used.
function readDevRoleOverride(): UserRole | null {
  if (!import.meta.env.DEV) return null
  if (typeof window === 'undefined') return null
  const param = new URLSearchParams(window.location.search).get('role')
  if (param === 'member' || param === 'dev' || param === 'admin') {
    return param
  }
  return null
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Check for token in URL (from OAuth callback)
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const refreshToken = params.get('refresh_token')
    const errorParam = params.get('error')

    if (token) {
      setAuthToken(token)
      if (refreshToken) {
        setRefreshToken(refreshToken)
      }
      // Clean URL — but preserve `?role=` so the dev override survives
      // the OAuth redirect.
      const devRole = params.get('role')
      const cleanSearch = devRole ? `?role=${devRole}` : ''
      window.history.replaceState(
        {},
        document.title,
        `${window.location.pathname}${cleanSearch}`,
      )
    }

    if (errorParam === 'unauthorized') {
      setError('Access denied. Only authorized users can access this application.')
      setLoading(false)
      return
    }

    if (errorParam === 'auth_failed') {
      setError('Authentication failed. Please try again.')
      setLoading(false)
      return
    }

    if (!isAuthenticated()) {
      setLoading(false)
      return
    }

    // Fetch the real user (including role) from /auth/me. If the token is
    // invalid the helper clears it and returns null — we just drop into the
    // unauthenticated state and let the routing layer handle the redirect.
    getCurrentUser()
      .then(fetched => {
        if (!fetched) {
          setUser(null)
          setLoading(false)
          return
        }
        // DEV-ONLY: overlay the `?role=` override (if any) on top of the
        // real role returned by the backend. Inert in production.
        const devOverride = readDevRoleOverride()
        setUser({
          email: fetched.email,
          role: devOverride ?? fetched.role,
        })
        setLoading(false)
      })
      .catch(() => {
        setUser(null)
        setLoading(false)
      })
  }, [])

  // Default to least privilege (`member`) until the user is loaded /
  // authenticated.
  const role: UserRole = user?.role ?? 'member'

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    role,
    hasRole: (minimum: UserRole) => meetsMinimum(user?.role, minimum),
    login: initiateLogin,
    logout,
  }
}
